/**
 * Shared utilities for world builder components.
 * Consolidates duplicated logic for auth, image operations, and API calls.
 */
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { toast } from "sonner";

// ── Auth ────────────────────────────────────────────────────────────

/** Get the current auth session or throw. */
export async function getAuthSession() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Not authenticated");
  return session;
}

/** Get current user or throw. Uses cached session — no network round-trip. */
export async function getAuthUser() {
  const user = await getSessionUser();
  if (!user) throw new Error("Not authenticated");
  return user;
}

// ── World Context ───────────────────────────────────────────────────

export interface WorldContext {
  name: string;
  description: string | null;
  setting: string;
  tone: string;
  rules: string;
}

/** Fetch world metadata for use in prompts. Returns formatted context string. */
export async function fetchWorldContext(worldId: string, maxLength = 1200): Promise<string> {
  const { data: world } = await supabase
    .from("worlds")
    .select("name, description, setting, tone, rules")
    .eq("id", worldId)
    .single();

  if (!world) return "";
  return [
    world.name ? `World: "${world.name}"` : "",
    world.description ? `Story: ${world.description.substring(0, 400)}` : "",
    world.setting ? `Setting: ${world.setting.substring(0, 400)}` : "",
    world.tone ? `Tone: ${world.tone}` : "",
    world.rules ? `Rules/Lore: ${world.rules.substring(0, 200)}` : "",
  ].filter(Boolean).join(". ").substring(0, maxLength);
}

// ── Image Operations ────────────────────────────────────────────────

/** Download an image by URL and trigger a browser save dialog. */
export async function downloadImage(imageUrl: string, fileName: string, errorMessage = "Download failed") {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName.replace(/[^a-zA-Z0-9]/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    toast.error(errorMessage);
  }
}

/** Upload an image file to the world-media storage bucket. Returns public URL. */
export async function uploadImageToStorage(file: File, worldId: string): Promise<string> {
  const user = await getAuthUser();
  const ext = file.name.split(".").pop()?.replace(/[^a-zA-Z0-9]/g, "") || "png";
  const fileName = `${user.id}/${worldId}/seed_${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from("world-media")
    .upload(fileName, file, { contentType: file.type, upsert: false });
  if (error) throw error;
  const { data: publicUrl } = supabase.storage.from("world-media").getPublicUrl(fileName);
  return publicUrl.publicUrl;
}

// ── Edge Function Calls ─────────────────────────────────────────────

import { functionUrl as edgeFunctionUrl } from "@/lib/supabase-env";

interface GenerateBriefParams {
  subject: string;
  entityName: string;
  type?: string;
  worldContext: string;
  webSearch?: boolean;
  searchKeywords?: string;
  maxSerpQueries?: number;
  skipQueries?: string[];
  fandomUrl?: string;
}

export interface BriefResult {
  brief: string;
  reference_images?: string[];
  reference_thumbnails?: string[];
  search_queries?: string[];
}

export interface BriefProgressEvent {
  step: string;
  detail?: string;
  query?: string;
  provider?: string;
  textsFound?: number;
  imagesFound?: number;
}

/** Call the generate-brief edge function with optional SSE streaming for progress. Auto-retries on 429/timeout. */
export async function generateBrief(
  params: GenerateBriefParams,
  onProgress?: (event: BriefProgressEvent) => void,
): Promise<BriefResult> {
  const session = await getAuthSession();
  const useStream = params.webSearch && !!onProgress;

  const MAX_RETRIES = 2;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      const backoff = Math.min(3000 * Math.pow(2, attempt - 1), 12000);
      onProgress?.({ step: "retrying", detail: `Retrying in ${Math.round(backoff / 1000)}s... (attempt ${attempt + 1})` });
      await new Promise(r => setTimeout(r, backoff));
    }

    try {
      const resp = await fetch(edgeFunctionUrl("generate-brief"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          subject: params.subject,
          entity_name: params.entityName,
          type: params.type || "portrait",
          image_type: params.type || "portrait",
          world_context: params.worldContext,
          web_search: params.webSearch || false,
          search_keywords: params.searchKeywords || undefined,
          stream: useStream,
          max_serp_queries: params.maxSerpQueries || 1,
          skip_queries: params.skipQueries || [],
          fandom_url: params.fandomUrl || undefined,
        }),
      });

      if (resp.status === 429 && attempt < MAX_RETRIES) {
        lastError = new Error("Rate limited");
        continue;
      }
      if (resp.status === 402) {
        throw new Error("AI credits exhausted. Please add credits in Settings → Workspace → Usage.");
      }
      if (!resp.ok) throw new Error(`Failed to generate brief (${resp.status})`);

      // Non-streaming: simple JSON response
      if (!useStream || !resp.body) {
        return resp.json();
      }

      // Streaming: parse SSE events
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let result: BriefResult = { brief: "" };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        let eventType = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith("data: ") && eventType) {
            try {
              const data = JSON.parse(line.slice(6));
              if (eventType === "progress" && onProgress) {
                onProgress(data);
              } else if (eventType === "result") {
                result = {
                  brief: data.brief || "",
                  reference_images: data.reference_images,
                  reference_thumbnails: data.reference_thumbnails,
                  search_queries: data.search_queries,
                };
              } else if (eventType === "error") {
                throw new Error(data.error || "Brief generation failed");
              }
            } catch (e) {
              if (e instanceof Error && e.message !== "Brief generation failed") {
                // JSON parse error, skip
              } else {
                throw e;
              }
            }
            eventType = "";
          }
        }
      }

      return result;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt >= MAX_RETRIES) throw lastError;
    }
  }

  throw lastError || new Error("Brief generation failed");
}

interface GenerateImageParams {
  worldId: string;
  type: string;
  title: string;
  description: string;
  entityId?: string | null;
  seedImageUrl?: string;
  creativityLevel?: number;
  additionalReferenceUrls?: string[];
  /** Pre-computed brief from generate-brief. If provided, skips internal brief generation. */
  precomputedBrief?: string;
  /** Image generation model to use. */
  model?: string;
}

export interface ImageResult {
  image_url: string;
}

/** Call the generate-world-image edge function. Auto-retries on 429/timeout. */
export async function generateWorldImage(params: GenerateImageParams): Promise<ImageResult> {
  const session = await getAuthSession();
  const MAX_RETRIES = 2;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      const backoff = Math.min(3000 * Math.pow(2, attempt - 1), 12000);
      await new Promise(r => setTimeout(r, backoff));
    }

    const resp = await fetch(edgeFunctionUrl("generate-world-image"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({
        world_id: params.worldId,
        type: params.type,
        title: params.title,
        description: params.description,
        entity_id: (params.entityId && !params.entityId.startsWith("banner-")) ? params.entityId : undefined,
        seed_image_url: params.seedImageUrl || undefined,
        creativity_level: params.seedImageUrl ? params.creativityLevel : undefined,
        additional_reference_urls: params.additionalReferenceUrls?.length ? params.additionalReferenceUrls : undefined,
        precomputed_brief: params.precomputedBrief || undefined,
        model: params.model || undefined,
      }),
    });

    if (resp.status === 429 && attempt < MAX_RETRIES) continue;
    if (resp.status === 402) {
      throw new Error("AI credits exhausted. Please add credits in Settings → Workspace → Usage.");
    }
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: `Failed: ${resp.status}` }));
      if (attempt < MAX_RETRIES && resp.status >= 500) continue;
      throw new Error(err.error || `Failed: ${resp.status}`);
    }
    return resp.json();
  }

  throw new Error("⚠️ The agent didn't produce a response. This can happen due to network issues or timeout. You can retry your last message.");
}

/** Map entity type to the best image generation type. */
export function entityTypeToImageType(entityType?: string): string {
  const t = (entityType || "").toLowerCase();
  if (t === "map") return "map";
  if (["location", "place", "planet", "region", "city", "building", "ship", "vehicle", "stargate"].includes(t)) return "scene";
  if (["item", "weapon", "artifact", "technology", "device", "object"].includes(t)) return "icon";
  if (["faction", "organization", "group", "species", "race"].includes(t)) return "banner";
  if (["event", "battle", "mission"].includes(t)) return "scene";
  return "portrait"; // characters, NPCs, etc.
}

/** Get a display label for the image type. */
export function imageTypeLabel(imageType: string): string {
  const labels: Record<string, string> = {
    portrait: "Portrait", scene: "Scene", map: "Map", banner: "Banner", icon: "Icon", card: "Card",
  };
  return labels[imageType] || "Image";
}

/** Convenience: generate an image for an entity with auto-briefing and web search. */
export async function generateEntityPortrait(
  worldId: string,
  entity: { id: string; name: string; description: string; type?: string },
): Promise<string> {
  const imageType = entityTypeToImageType(entity.type);
  const worldContext = await fetchWorldContext(worldId, 600);
  const briefResult = await generateBrief({
    subject: `${entity.name}: ${entity.description}`,
    entityName: entity.name,
    type: imageType,
    worldContext,
    webSearch: true,
  });
  const brief = briefResult.brief || `${entity.name}: ${entity.description}`;
  const referenceUrls = briefResult.reference_images?.filter(u => u.startsWith("http")) || [];
  const result = await generateWorldImage({
    worldId,
    type: imageType,
    title: entity.name,
    description: `${entity.name}: ${entity.description}`,
    entityId: entity.id,
    additionalReferenceUrls: referenceUrls.length > 0 ? referenceUrls : undefined,
    precomputedBrief: brief, // Pass precomputed brief to skip redundant brief generation
  });
  return result.image_url;
}

// ── Codex Search for Brief ──────────────────────────────────────────

export interface CodexSearchResult {
  snippets: string[];
  entityImages: string[];
}

/**
 * Search the world's RAG index (document_chunks) + entities table
 * to gather lore context and existing entity images for brief enrichment.
 */
export async function searchCodexForBrief(
  worldId: string,
  keywords: string,
  matchCount = 8,
): Promise<CodexSearchResult> {
  const snippets: string[] = [];
  const entityImages: string[] = [];

  // 1. RAG search via search_document_chunks RPC
  try {
    const { data: chunks } = await supabase.rpc("search_document_chunks", {
      _world_id: worldId,
      _query_text: keywords,
      _match_count: matchCount,
    });
    if (chunks?.length) {
      for (const chunk of chunks) {
        if (chunk.content) snippets.push(chunk.content);
      }
    }
  } catch (err) {
    console.warn("Codex RAG search failed:", err);
  }

  // 2. Entity image lookup — find entities whose names match keywords
  try {
    // Use ilike for simple keyword matching across entity names
    const terms = keywords.split(/[\s,]+/).filter(Boolean).slice(0, 5);
    // S-14: strip PostgREST operator characters so user-supplied keywords cannot
    // inject extra filter clauses into the .or() call.
    const safeTerms = terms.map((t) => t.replace(/[(),."'\\%_]/g, "").trim()).filter(Boolean);
    if (!safeTerms.length) throw new Error("no safe search terms");
    const orFilter = safeTerms.map((t) => `name.ilike.%${t}%`).join(",");
    const { data: entities } = await supabase
      .from("entities")
      .select("id, name, image_url, description")
      .eq("world_id", worldId)
      .is("deleted_at", null)
      .not("image_url", "is", null)
      .or(orFilter)
      .limit(10);

    if (entities?.length) {
      for (const ent of entities) {
        if (ent.image_url) entityImages.push(ent.image_url);
        // Also add entity descriptions as context snippets
        if (ent.description) snippets.push(`[Entity: ${ent.name}] ${ent.description}`);
      }
    }
  } catch (err) {
    console.warn("Entity image lookup failed:", err);
  }

  return { snippets, entityImages };
}
